import React from 'react';
import { FiCheck } from 'react-icons/fi';
import { buildDeliveryGuideLineKey, buildDeliveryGuideLineKeys } from '../utils/deliveryGuideItems';

const defaultRemaining = (line) => line.remainingQuantity || 0;

const defaultLabels = {
  driverName: 'Nombre del chofer',
  vehiclePlate: 'Patente del vehiculo',
  deliveryDate: 'Fecha de entrega',
  notes: 'Notas',
  product: 'Producto',
  unit: 'Unidad',
  ordered: 'Pedido',
  delivered: 'Entregado',
  remaining: 'Restante',
  currentGuide: 'En esta guia',
  setMaxAria: 'Usar maximo disponible para este producto',
};

const DeliveryGuideForm = ({
  onSubmit,
  summary = [],
  itemsToDeliver = {},
  canEdit = true,
  driverName,
  onDriverNameChange,
  vehiclePlate,
  onVehiclePlateChange,
  deliveryDate,
  onDeliveryDateChange,
  notes,
  onNotesChange,
  itemsTitle = 'Productos a despachar',
  emptyMessage = 'No hay productos pendientes para esta compra.',
  getMaxForLine = defaultRemaining,
  getRemainingForLine = defaultRemaining,
  onQuantityChange,
  onSetMax,
  labels,
  footerLeft,
  footerRight,
  detailsWrapperClassName = 'bg-white rounded-lg shadow p-4 mb-4',
  tableWrapperClassName = 'bg-white rounded-lg shadow p-4 mb-4 overflow-x-auto',
  footerClassName = 'flex justify-between mt-4',
}) => {
  const hasFooter = footerLeft || footerRight;
  const mergedLabels = { ...defaultLabels, ...(labels || {}) };

  return (
    <form onSubmit={onSubmit}>
      <div className={detailsWrapperClassName}>
        <h2 className="text-lg font-semibold mb-3">Datos del despacho</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mergedLabels.driverName}
            </label>
            <input
              type="text"
              value={driverName}
              onChange={onDriverNameChange}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mergedLabels.vehiclePlate}
            </label>
            <input
              type="text"
              value={vehiclePlate}
              onChange={onVehiclePlateChange}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mergedLabels.deliveryDate}
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={onDeliveryDateChange}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mergedLabels.notes}
            </label>
            <textarea
              value={notes}
              onChange={onNotesChange}
              rows={3}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className={tableWrapperClassName}>
        <h2 className="text-lg font-semibold mb-3">{itemsTitle}</h2>
        {summary.length === 0 ? (
          <p className="text-sm text-gray-600">{emptyMessage}</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-600">
                  {mergedLabels.product}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">
                  {mergedLabels.unit}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {mergedLabels.ordered}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {mergedLabels.delivered}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {mergedLabels.remaining}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {mergedLabels.currentGuide}
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.map((line) => {
                const key = buildDeliveryGuideLineKey(line);
                const remaining = getRemainingForLine(line);
                const maxForThisGuide = getMaxForLine(line);
                const value =
                  buildDeliveryGuideLineKeys(line).reduce((acc, lineKey) => {
                    if (acc !== undefined) return acc;
                    return itemsToDeliver[lineKey];
                  }, undefined) ?? '';
                return (
                  <tr key={key} className="border-t">
                    <td className="px-3 py-2">{line.varietyName}</td>
                    <td className="px-3 py-2">{line.metricUnit || 'unidad'}</td>
                    <td className="px-3 py-2 text-right">{line.orderedQuantity}</td>
                    <td className="px-3 py-2 text-right">{line.deliveredSoFar}</td>
                    <td className="px-3 py-2 text-right">{remaining}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={value}
                          onChange={(event) =>
                            onQuantityChange?.(key, maxForThisGuide, event.target.value)
                          }
                          disabled={!canEdit}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-right"
                        />
                        <button
                          type="button"
                          onClick={() => onSetMax?.(key, maxForThisGuide)}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded"
                          disabled={!canEdit || maxForThisGuide <= 0}
                          aria-label={mergedLabels.setMaxAria}
                        >
                          <FiCheck className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {hasFooter && (
        <div className={footerClassName}>
          <div>{footerLeft}</div>
          <div>{footerRight}</div>
        </div>
      )}
    </form>
  );
};

export default DeliveryGuideForm;
